import {useEffect} from 'react';
import {useFormContext, useFieldArray} from 'react-hook-form';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@common-web/components/ui/form';
import {Input} from '@common-web/components/ui/input';
import {Separator} from '@common-web/components/ui/separator';
import {Button} from '@common-web/components/ui/button';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {AddressFields} from '../components/AddressFields';
import {StateSelect} from '../components/StateSelect';
import {MapPin, Copy, ChevronDown, ChevronUp} from 'lucide-react';
import {useState} from 'react';
import {getBlankLocation} from '@common/lib/rfcSchema';

export function Section125GroupInfo() {
  const {control, watch, setValue, getValues} = useFormContext();
  const numberOfLocations = watch('section125Form.groupInfo.numberOfLocations');
  const {fields, append, replace} = useFieldArray({
    control,
    name: 'section125Form.groupInfo.locations',
  });

  useEffect(() => {
    const count = typeof numberOfLocations === 'number' ? numberOfLocations : 0;
    if (count < 1) return;
    const currentLocations =
      (getValues('section125Form.groupInfo.locations') as ReturnType<typeof getBlankLocation>[]) ||
      [];
    if (count === currentLocations.length) return;
    if (count > currentLocations.length) {
      for (let i = currentLocations.length; i < count; i++) {
        append(getBlankLocation());
      }
    } else {
      replace(currentLocations.slice(0, count));
    }
  }, [numberOfLocations]);

  return (
    <div className='space-y-6'>
      <div className='space-y-4'>
        <div className='flex flex-col gap-4 sm:flex-row sm:items-start'>
          <FormField
            control={control}
            name={'section125Form.groupInfo.benefitYear'}
            render={({field}) => (
              <FormItem className='sm:w-36 sm:shrink-0'>
                <FormLabel>Benefit Year *</FormLabel>
                <FormControl>
                  <Input placeholder='YYYY' {...field} inputMode='numeric' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={'section125Form.groupInfo.companyName'}
            render={({field}) => (
              <FormItem className='min-w-0 flex-1'>
                <FormLabel>Company Legal Name *</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <FormField
            control={control}
            name={'section125Form.groupInfo.companyDba'}
            render={({field}) => (
              <FormItem>
                <FormLabel>Company DBA (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={'section125Form.groupInfo.otherKnownName'}
            render={({field}) => (
              <FormItem>
                <FormLabel>Other Known Name (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={'section125Form.groupInfo.sic'}
            render={({field}) => (
              <FormItem>
                <FormLabel>SIC Code (Optional)</FormLabel>
                <FormControl>
                  <Input inputMode='numeric' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={'section125Form.groupInfo.totalEmployees'}
            render={({field}) => (
              <FormItem>
                <FormLabel>Total # of Employees *</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? '' : e.target.valueAsNumber)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <StateSelect
            name={'section125Form.groupInfo.stateOfDoingBusiness'}
            label='Primary State of Doing Business *'
          />
        </div>
      </div>

      <Separator />

      <div className='space-y-4'>
        <div className='flex items-center gap-4'>
          <FormField
            control={control}
            name={'section125Form.groupInfo.numberOfLocations'}
            render={({field}) => (
              <FormItem className='flex-1 max-w-xs'>
                <FormLabel>Number of Locations *</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    min={1}
                    max={100}
                    {...field}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? '' : e.target.valueAsNumber)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {fields.length > 0 && (
          <div className='space-y-4'>
            <div className='flex items-center gap-2'>
              <MapPin className='h-5 w-5 text-brand-blue' />
              <h3 className='text-lg font-semibold text-brand-navy'>Locations</h3>
              <span className='text-sm text-brand-gray'>
                (Location 1 is your primary location — its FEIN and company address are required.
                Other location details can be completed later)
              </span>
            </div>
            {fields.map((field, index) => (
              <LocationCard
                key={field.id}
                index={index}
                basePath={`section125Form.groupInfo.locations.${index}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LocationCard({index, basePath}: {index: number; basePath: string}) {
  const {control, getValues, setValue} = useFormContext();
  const [showContact, setShowContact] = useState(true);

  const copyCompanyAddressToMailing = () => {
    const companyAddress = getValues(`${basePath}.companyAddress`);
    if (companyAddress) {
      setValue(`${basePath}.mailingAddress`, companyAddress, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  };

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b py-3'>
        <CardTitle className='text-base font-semibold text-brand-navy'>
          Location {index + 1}
        </CardTitle>
      </CardHeader>
      <CardContent className='space-y-4 pt-4'>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <FormField
            control={control}
            name={`${basePath}.locationName`}
            render={({field}) => (
              <FormItem>
                <FormLabel>Location Name</FormLabel>
                <FormControl>
                  <Input placeholder='e.g. Main Office, Warehouse' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.fein`}
            render={({field}) => (
              <FormItem>
                <FormLabel>
                  {index === 0 ? 'Company FEIN (9 digits) *' : 'FEIN (9 digits)'}
                </FormLabel>
                <FormControl>
                  <Input
                    inputMode='numeric'
                    placeholder={index === 0 ? undefined : 'Optional'}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name={`${basePath}.payrollLocationCode`}
            render={({field}) => (
              <FormItem>
                <FormLabel>Payroll Location Code (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value ?? ''} />
                </FormControl>
                <FormDescription>
                  Company name or code assigned by your payroll company to identify this location
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className='space-y-2'>
          <div className='font-medium text-sm text-brand-navy'>
            Company Address{index === 0 ? ' *' : ''}
          </div>
          <AddressFields basePath={`${basePath}.companyAddress`} required={index === 0} />
        </div>
        <div className='space-y-2'>
          <div className='flex items-center gap-4 justify-start'>
            <div className='font-medium text-sm text-brand-navy'>Mailing Address</div>
            <Button type='button' size='sm' variant='outline' onClick={copyCompanyAddressToMailing}>
              <Copy className='h-3 w-3 mr-1' />
              Copy company address
            </Button>
          </div>
          <AddressFields basePath={`${basePath}.mailingAddress`} />
        </div>
        <div className='space-y-2'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            onClick={() => setShowContact(!showContact)}
            className='text-brand-gray hover:text-brand-navy'
          >
            {showContact ? (
              <ChevronUp className='h-4 w-4 mr-1' />
            ) : (
              <ChevronDown className='h-4 w-4 mr-1' />
            )}
            Contact Person
          </Button>
          {showContact && (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 pl-2'>
              <FormField
                control={control}
                name={`${basePath}.contactPerson.firstName`}
                render={({field}) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${basePath}.contactPerson.lastName`}
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${basePath}.contactPerson.title`}
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${basePath}.contactPerson.phone`}
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`${basePath}.contactPerson.email`}
                render={({field}) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type='email' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
